import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import strings from "../global/strings";
import type { BilledItem } from "../components/invoice-template/invoice-template.component";
import type { Customer } from "./customer.queries";

export const draftsQueryKey = ["drafts"] as const;

// Mirrors billing_system_BE/models/draft.js in full — the row shape returned
// by draft/company, draft/add, draft/update, draft/delete (all delegate to the
// same helper_getGroupedDraft/getCompanyDraft grouping, see DraftsGroupedResult
// below). customer_info is the CompanyCustomers association (as: "customer_info"
// in Draft.associate), attributes minus createdAt/updatedAt.
export interface Draft {
	draft_id: string;
	company_info_id: string;
	company_user_id: string;
	company_customer_id?: string | null;
	customer_mobile?: string | null;
	customer_gender?: string | null;
	customer_type: string;
	customer_address?: string | null;
	customer_city?: string | null;
	customer_email?: string | null;
	record_id?: number | null;
	record_type?: string | null;
	buyer_pan?: string | null;
	buyer_name: string;
	bill_for: string;
	fiscal_year?: string | null;
	invoice_number?: string | null;
	invoice_date_nepali?: string | null;
	invoice_date?: string | null;
	sales_date: string;
	sub_total: number;
	total_sales: number;
	payment_mode: string;
	non_taxable_amt: number;
	taxable_sales_vat: number;
	non_taxable_discount: number;
	taxable_discount: number;
	non_taxable_service_charge: number;
	taxable_service_charge: number;
	vat: number;
	excisable_amount: number;
	excise: number;
	taxable_sales_hst: number;
	hst: number;
	amount_for_esf: number;
	esf: number;
	export_sales: number;
	tax_exempted_sales: number;
	sale_info?: string | null;
	isrealtime: boolean;
	datetimeClient: string;
	draft_valid: boolean;
	billed_items: BilledItem[];
	draft_bill_status: "draft" | "ready" | "failed" | "locked";
	customer_info?: Omit<Customer, "createdAt" | "updatedAt"> | null;
	createdAt: string;
	updatedAt: string;
}

// draft/company, draft/add, draft/update, and draft/delete all return this
// exact shape (bill.controller.js: getCompanyDraft groups by draft_bill_status,
// addDraft/deleteDraft delegate to it, updateDraft uses the equivalent
// helper_getGroupedDraft) — one response type for all four endpoints.
export interface DraftsGroupedResult {
	draft: Draft[];
	ready: Draft[];
	failed: Draft[];
	locked: Draft[];
}

const EMPTY_GROUPED: DraftsGroupedResult = {
	draft: [],
	ready: [],
	failed: [],
	locked: [],
};

// Mirrors draftsReducer's FETCH_DRAFT_SUCCESS derivation exactly (draftsList =
// draft, readyDraftsList = failed+locked+ready, errorDraftsList = failed+locked)
// — centralized here so draft-list and ready-draft-list don't each reimplement it.
export function deriveDraftLists(grouped: DraftsGroupedResult | undefined) {
	const { draft, ready, failed, locked } = grouped ?? EMPTY_GROUPED;
	return {
		draftsList: draft,
		readyDraftsList: [...failed, ...locked, ...ready],
		errorDraftsList: [...failed, ...locked],
	};
}

async function fetchDraftsGrouped(): Promise<DraftsGroupedResult> {
	const res = await axios.get(`${strings.url}draft/company`, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

// Shared, cached read — draft-list and ready-draft-list both call this same
// hook/queryKey (React Query dedupes into one request).
export function useDrafts() {
	return useQuery({
		queryKey: draftsQueryKey,
		queryFn: fetchDraftsGrouped,
	});
}

// Mirrors billing_system_BE/models/draft.js, scoped to the fields
// customer-bulk-draft/bill-duplicate/invoice-form actually send when creating
// a NEW draft. Full Draft type belongs to a future bill.queries.ts (Batch 5-C)
// once drafts/bills are unified there.
export interface DraftPayload {
	company_customer_id?: string | null;
	customer_name?: string;
	customer_pan_vat?: string | null;
	customer_address?: string | null;
	customer_city?: string | null;
	customer_email?: string | null;
	customer_mobile?: string | null;
	customer_type?: string;
	customer_gender?: string | null;
	buyer_name?: string;
	buyer_pan?: string | null;
	bill_for?: string;
	invoice_date?: string;
	sales_date?: string;
	payment_mode?: string;
	sub_total?: number;
	total_sales?: number;
	taxable_total_sales?: number;
	non_taxable_total_sales?: number;
	non_taxable_amt?: number;
	taxable_sales_vat?: number;
	vat?: number;
	non_taxable_service_charge?: number;
	taxable_service_charge?: number;
	amount_for_esf?: number;
	esf?: number;
	non_taxable_discount?: number;
	taxable_discount?: number;
	draft_valid?: boolean;
	sale_info?: string;
	billed_items?: BilledItem[];
}

export interface SaveDraftVars {
	// addDraft (billing_system_BE controllers/bill.controller.js:149) branches
	// on draft.draft_id: no id -> Draft.create (a fresh DraftPayload, the
	// customer-bulk-draft/bill-duplicate/invoice-form case), id present ->
	// Draft.update (promoting an already-fetched Draft row to "ready"/"draft",
	// the draft-list bulk-save/bulk-ready case) — so this accepts either shape.
	drafts: Array<DraftPayload | Partial<Draft>>;
	draft_bill_status: string;
	// Set by bill-duplicate.component.tsx: tells addDraft (billing_system_BE
	// controllers/bill.controller.js) to stamp draft_valid: true and this
	// invoice_date onto every created draft, instead of leaving drafts in
	// their normal (not-yet-ready) state.
	duplicateBill?: boolean;
	invoice_date?: string;
}

// addDraft (billing_system_BE controllers/bill.controller.js:224) delegates to
// getCompanyDraft for its response — result is the same DraftsGroupedResult
// shape draft/company returns, not a flat array of the created drafts.
async function postSaveDraft(vars: SaveDraftVars): Promise<DraftsGroupedResult> {
	const res = await axios.post(`${strings.url}draft/add`, vars, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

// No toast — callers render their own FormError/SuccessDisplay (or a brief
// SuccessDisplay before navigating away, for customer-bulk-draft/bill-duplicate)
// bound to this mutation's own isError/isSuccess. Writes the response straight
// into the shared drafts cache (setQueryData, not invalidate) so any caller's
// save is immediately visible to draft-list/ready-draft-list regardless of
// which one triggered it — same precise-cache-write approach as
// useUpdateDraft/useDeleteDraft.
export function useSaveDraft() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: postSaveDraft,
		onSuccess: (result) => {
			queryClient.setQueryData(draftsQueryKey, result);
		},
	});
}

export interface UpdateDraftVars {
	// Always full/partial existing rows (fetched via useDrafts then locally
	// mutated), never freshly constructed like a DraftPayload add — draft_id is
	// required server-side (bill.controller.js:updateDraft matches on it).
	drafts: Partial<Draft>[];
	draft_bill_status: string;
}

async function putUpdateDraft(vars: UpdateDraftVars): Promise<DraftsGroupedResult> {
	const res = await axios.put(`${strings.url}draft/update`, vars, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useUpdateDraft() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: putUpdateDraft,
		onSuccess: (result) => {
			queryClient.setQueryData(draftsQueryKey, result);
		},
	});
}

export interface DeleteDraftVars {
	// bill.controller.js:deleteDraft only reads draft_id per item, but the FE
	// always has the full row in hand (selected straight out of draftsList), so
	// this matches UpdateDraftVars rather than narrowing to Pick<Draft, "draft_id">.
	drafts: Partial<Draft>[];
}

async function putDeleteDraft(vars: DeleteDraftVars): Promise<DraftsGroupedResult> {
	const res = await axios.put(`${strings.url}draft/delete`, vars, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useDeleteDraft() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: putDeleteDraft,
		onSuccess: (result) => {
			queryClient.setQueryData(draftsQueryKey, result);
		},
	});
}
